import { 
  ProjectTypeDefinition, 
  ProjectPlanningCategory,
  PlanningStep,
  ProjectTypeField,
  ArtifactType,
  ValidationRule
} from '../types/project-types';

// Konfigurationsdefinitionen für alle 5 Projekttypen
export const PROJECT_TYPE_DEFINITIONS: Record<ProjectPlanningCategory, ProjectTypeDefinition> = {
  
  // 1) Lernziel – Backward Design (Didaktik)
  [ProjectPlanningCategory.LEARNING_GOAL]: {
    category: ProjectPlanningCategory.LEARNING_GOAL,
    label: 'Lernziel – Backward Design',
    description: 'Für Kompetenzentwicklung und Prüfungsvorbereitung mit Fokus auf Lernergebnisse',
    characteristics: {
      triggerType: 'outcome',
      timeHorizon: 'medium',
      measurementFocus: 'learning'
    },
    planningSteps: [
      {
        order: 1,
        name: 'Lernergebnis definieren',
        description: 'Präzise Definition der zu erreichenden Kompetenz oder Prüfung',
        isRequired: true
      },
      {
        order: 2,
        name: 'Beurteilungsformen festlegen',
        description: 'Assessments, Rubrics und Bewertungskriterien definieren',
        isRequired: true
      },
      {
        order: 3,
        name: 'Lernpfad rückwärts ableiten',
        description: 'Module, Aktivitäten und Ressourcen vom Ziel her planen',
        isRequired: true
      }
    ],
    requiredFields: [
      {
        key: 'learningOutcome',
        label: 'Lernergebnis',
        type: 'text',
        required: true
      },
      {
        key: 'competencyLevel',
        label: 'Kompetenzniveau (1-10)',
        type: 'number',
        required: true,
        validation: { min: 1, max: 10 }
      },
      {
        key: 'assessmentMethods',
        label: 'Bewertungsmethoden',
        type: 'multi-select',
        required: true,
        options: ['Prüfung', 'Projekt', 'Portfolio', 'Präsentation', 'Peer-Review']
      }
    ],
    artifactTypes: [
      { name: 'Lernergebnisse', description: 'Konkrete Outcomes', isRequired: true },
      { name: 'Assessment-Rubrik', description: 'Bewertungskriterien', isRequired: true },
      { name: 'Lernmodule', description: 'Strukturierte Lerneinheiten', isRequired: true },
      { name: 'Ressourcenliste', description: 'Lernmaterialien und Links', isRequired: false }
    ],
    validationRules: [
      {
        field: 'learningOutcome',
        rule: 'required',
        errorMessage: 'Lernergebnis muss definiert werden'
      }
    ],
    aiPromptTemplate: `Erstelle eine detaillierte Backward-Design Lernplanung für: {description}
    
Ziel-Kompetenz: {learningOutcome}
Niveau: {competencyLevel}/10
Bewertung: {assessmentMethods}

Plane rückwärts vom Lernergebnis:
1. Konkrete Outcomes mit messbaren Kriterien
2. Passende Assessment-Methoden und Rubrics  
3. Strukturierte Module und Lernaktivitäten
4. Ressourcen und Materialien

Berücksichtige didaktische Prinzipien und Lernpfade.`
  },

  // 2) Projektziel – Backward Scheduling (Meilensteine)
  [ProjectPlanningCategory.PROJECT_GOAL]: {
    category: ProjectPlanningCategory.PROJECT_GOAL,
    label: 'Projektziel – Backward Scheduling',
    description: 'Für Lieferobjekte, Releases und Events mit festen Deadlines',
    characteristics: {
      triggerType: 'deadline',
      timeHorizon: 'short',
      measurementFocus: 'completion'
    },
    planningSteps: [
      {
        order: 1,
        name: 'Endtermin fixieren',
        description: 'Harte Deadline und finale Deliverables definieren',
        isRequired: true
      },
      {
        order: 2,
        name: 'Meilensteine rückwärts planen',
        description: 'Kritische Zwischenziele mit Deadlines festlegen',
        isRequired: true
      },
      {
        order: 3,
        name: 'Tasks je Meilenstein',
        description: 'Detaillierte Aufgaben mit Dauer, Abhängigkeiten und Verantwortlichkeiten',
        isRequired: true
      }
    ],
    requiredFields: [
      {
        key: 'finalDeadline',
        label: 'Endtermin',
        type: 'date',
        required: true
      },
      {
        key: 'deliverables',
        label: 'Lieferobjekte',
        type: 'multi-select',
        required: true,
        options: ['Software Release', 'Produktlaunch', 'Event', 'Dokument', 'Präsentation']
      },
      {
        key: 'stakeholders',
        label: 'Stakeholder',
        type: 'text',
        required: false
      },
      {
        key: 'budget',
        label: 'Budget',
        type: 'number',
        required: false
      }
    ],
    artifactTypes: [
      { name: 'Meilensteine', description: 'Zeitbasierte Zwischenziele', isRequired: true },
      { name: 'Task-Liste', description: 'Detaillierte Aufgaben mit Verantwortlichkeiten', isRequired: true },
      { name: 'Abhängigkeiten', description: 'Kritischer Pfad und Blocking-Dependencies', isRequired: true },
      { name: 'Gantt-Diagramm', description: 'Visueller Projektplan', isRequired: false }
    ],
    validationRules: [
      {
        field: 'finalDeadline',
        rule: 'date_future',
        errorMessage: 'Endtermin muss in der Zukunft liegen'
      }
    ],
    aiPromptTemplate: `Erstelle einen detaillierten Backward-Scheduling Plan für: {description}

Deadline: {finalDeadline}
Deliverables: {deliverables}
Stakeholder: {stakeholders}
Budget: {budget}

Plane rückwärts vom Endtermin:
1. Kritische Meilensteine mit konkreten Deadlines
2. Detaillierte Tasks je Meilenstein (Dauer, Owner, Dependencies)
3. Kritischen Pfad und Risiko-Dependencies identifizieren
4. Buffer-Zeiten und Contingency-Pläne

Berücksichtige Projektmanagement best practices und realistische Zeitschätzungen.`
  },

  // 3) Persönliches Ziel – Goal Breakdown
  [ProjectPlanningCategory.PERSONAL_GOAL]: {
    category: ProjectPlanningCategory.PERSONAL_GOAL,
    label: 'Persönliches Ziel – Goal Breakdown',
    description: 'Für individuelle Entwicklung und Performance-Ziele',
    characteristics: {
      triggerType: 'milestone',
      timeHorizon: 'medium',
      measurementFocus: 'performance'
    },
    planningSteps: [
      {
        order: 1,
        name: 'Endzustand beschreiben',
        description: 'SMART-Ziel mit konkreten Erfolgskriterien definieren',
        isRequired: true
      },
      {
        order: 2,
        name: 'Etappenziele definieren',
        description: 'Messbare Zwischenziele in Monats/Wochenabständen',
        isRequired: true
      },
      {
        order: 3,
        name: 'Routinen ableiten',
        description: 'Tägliche/wöchentliche Gewohnheiten und Tasks entwickeln',
        isRequired: true
      }
    ],
    requiredFields: [
      {
        key: 'smartGoal',
        label: 'SMART-Ziel',
        type: 'text',
        required: true
      },
      {
        key: 'successMetrics',
        label: 'Erfolgsmessung',
        type: 'multi-select',
        required: true,
        options: ['Zeit', 'Qualität', 'Quantität', 'Häufigkeit', 'Feedback']
      },
      {
        key: 'timeFrame',
        label: 'Zeitrahmen',
        type: 'select',
        required: true,
        options: ['3 Monate', '6 Monate', '1 Jahr', '2 Jahre']
      },
      {
        key: 'motivation',
        label: 'Motivation/Warum',
        type: 'text',
        required: false
      }
    ],
    artifactTypes: [
      { name: 'SMART-Zieldefinition', description: 'Spezifisches, messbares Endziel', isRequired: true },
      { name: 'Etappenziele', description: 'Monatliche/wöchentliche Zwischenziele', isRequired: true },
      { name: 'Routinen-Plan', description: 'Tägliche Gewohnheiten und Tasks', isRequired: true },
      { name: 'Tracking-System', description: 'Messpunkte und Erfolgs-Indikatoren', isRequired: false }
    ],
    validationRules: [
      {
        field: 'smartGoal',
        rule: 'required',
        errorMessage: 'SMART-Ziel muss definiert werden'
      }
    ],
    aiPromptTemplate: `Erstelle einen strukturierten Goal-Breakdown Plan für: {description}

SMART-Ziel: {smartGoal}
Erfolgsmessung: {successMetrics}
Zeitrahmen: {timeFrame}
Motivation: {motivation}

Strukturiere rückwärts vom Endziel:
1. SMART-Ziel mit messbaren Erfolgskriterien  
2. Etappenziele (Monats-/Wochenziele mit Messpunkten)
3. Konkrete Routinen und tägliche/wöchentliche Tasks
4. Tracking-System und Erfolgsindikatoren

Berücksichtige Motivation, realistische Zeitplanung und Gewohnheitsbildung.`
  },

  // 4) Vision/Zukunftsbild – Backcasting
  [ProjectPlanningCategory.VISION_GOAL]: {
    category: ProjectPlanningCategory.VISION_GOAL,
    label: 'Vision/Zukunftsbild – Backcasting',
    description: 'Für strategische Transformation und langfristige Visionen',
    characteristics: {
      triggerType: 'vision',
      timeHorizon: 'long',
      measurementFocus: 'transformation'
    },
    planningSteps: [
      {
        order: 1,
        name: 'Zukunftsbild präzisieren',
        description: 'Vision mit Leitplanken und Constraints definieren',
        isRequired: true
      },
      {
        order: 2,
        name: 'Schlüsselschritte definieren',
        description: 'Kritische Transformations-Meilensteine identifizieren',
        isRequired: true
      },
      {
        order: 3,
        name: 'Roadmap mit Szenarien',
        description: 'Flexible Wege mit Alternativen und Risikomanagement',
        isRequired: true
      }
    ],
    requiredFields: [
      {
        key: 'visionStatement',
        label: 'Visions-Statement',
        type: 'text',
        required: true
      },
      {
        key: 'timeHorizon',
        label: 'Zeithorizont',
        type: 'select',
        required: true,
        options: ['2 Jahre', '5 Jahre', '10 Jahre', '20+ Jahre']
      },
      {
        key: 'constraints',
        label: 'Leitplanken/Constraints',
        type: 'multi-select',
        required: true,
        options: ['Budget', 'Regulierung', 'Technologie', 'Markt', 'Personal', 'Nachhaltigkeit']
      },
      {
        key: 'stakeholderImpact',
        label: 'Stakeholder-Impact',
        type: 'text',
        required: false
      }
    ],
    artifactTypes: [
      { name: 'Vision-Definition', description: 'Präzises Zukunftsbild mit Leitplanken', isRequired: true },
      { name: 'Schlüsselinitiativen', description: 'Transformations-Meilensteine', isRequired: true },
      { name: 'Szenarien-Roadmap', description: 'Alternative Pfade und Optionen', isRequired: true },
      { name: 'Risk-Assessment', description: 'Risiken und Mitigation-Strategien', isRequired: false }
    ],
    validationRules: [
      {
        field: 'visionStatement',
        rule: 'required',
        errorMessage: 'Vision-Statement muss definiert werden'
      }
    ],
    aiPromptTemplate: `Erstelle einen Backcasting-Plan für die Vision: {description}

Vision: {visionStatement}
Zeithorizont: {timeHorizon}
Constraints: {constraints}
Stakeholder-Impact: {stakeholderImpact}

Entwickle rückwärts von der Vision:
1. Präzise Vision mit klaren Leitplanken und Erfolgskriterien
2. Kritische Schlüsselinitiativen und Transformations-Meilensteine  
3. Flexible Roadmap mit alternativen Pfaden und Szenarien
4. Risiko-Assessment und Contingency-Strategien

Berücksichtige strategische Planung, Unsicherheiten und verschiedene Zukunfts-Szenarien.`
  },

  // 5) Technisches Ziel – Reverse Planning/Engineering  
  [ProjectPlanningCategory.TECHNICAL_GOAL]: {
    category: ProjectPlanningCategory.TECHNICAL_GOAL,
    label: 'Technisches Ziel – Reverse Planning',
    description: 'Für System-Engineering und technische Implementierungen',
    characteristics: {
      triggerType: 'result',
      timeHorizon: 'short',
      measurementFocus: 'functionality'
    },
    planningSteps: [
      {
        order: 1,
        name: 'Zielformat definieren',
        description: 'Contracts, Tests und exaktes Verhalten spezifizieren',
        isRequired: true
      },
      {
        order: 2,
        name: 'Komponenten herleiten',
        description: 'Notwendige System-Teile und Datenflüsse rückwärts ableiten',
        isRequired: true
      },
      {
        order: 3,
        name: 'Prototyping/Spikes',
        description: 'Technische Risiken durch Experimente validieren',
        isRequired: true
      }
    ],
    requiredFields: [
      {
        key: 'targetBehavior',
        label: 'Ziel-Verhalten/Output',
        type: 'text',
        required: true
      },
      {
        key: 'techStack',
        label: 'Tech-Stack',
        type: 'multi-select',
        required: true,
        options: ['JavaScript', 'Python', 'Java', 'C++', 'React', 'Node.js', 'PostgreSQL', 'Redis']
      },
      {
        key: 'constraints',
        label: 'Technische Constraints',
        type: 'multi-select',
        required: false,
        options: ['Performance', 'Skalierung', 'Security', 'Compatibility', 'Maintainability']
      },
      {
        key: 'existingSystem',
        label: 'Bestehendes System',
        type: 'text',
        required: false
      }
    ],
    artifactTypes: [
      { name: 'System-Contracts', description: 'API-Specs und Interface-Definitionen', isRequired: true },
      { name: 'Architektur-Diagramm', description: 'Komponenten und Datenfluss-Design', isRequired: true },
      { name: 'Spike-Tasks', description: 'Technische Experimente zur Risiko-Validierung', isRequired: true },
      { name: 'Test-Suite', description: 'Automatisierte Tests für Ziel-Verhalten', isRequired: false }
    ],
    validationRules: [
      {
        field: 'targetBehavior',
        rule: 'required',
        errorMessage: 'Ziel-Verhalten muss spezifiziert werden'
      }
    ],
    aiPromptTemplate: `Erstelle einen Reverse-Engineering Plan für: {description}

Ziel-Verhalten: {targetBehavior}
Tech-Stack: {techStack}
Constraints: {constraints}
Bestehendes System: {existingSystem}

Plane rückwärts vom gewünschten Ergebnis:
1. Präzise Contracts, API-Specs und Ziel-Verhalten definieren
2. Notwendige Komponenten, Datenflüsse und Abhängigkeiten herleiten  
3. Technische Spikes zur Risiko- und Machbarkeits-Validierung
4. Test-first Ansatz mit automatisierten Tests

Berücksichtige Software-Engineering best practices, Architektur-Patterns und technische Risiken.`
  }
};

// Helper-Funktionen
export const getProjectTypeDefinition = (category: ProjectPlanningCategory): ProjectTypeDefinition => {
  return PROJECT_TYPE_DEFINITIONS[category];
};

export const getAllProjectTypes = (): ProjectTypeDefinition[] => {
  return Object.values(PROJECT_TYPE_DEFINITIONS);
};

export const getProjectTypeByLabel = (label: string): ProjectTypeDefinition | undefined => {
  return getAllProjectTypes().find(type => type.label === label);
};

// Projekttyp-Empfehlungs-Logic
export interface ProjectTypeRecommendation {
  category: ProjectPlanningCategory;
  confidence: number;
  reasoning: {
    keywords: string[];
    patterns: string[];
    indicators: string[];
  };
  explanation: string;
}

export const recommendProjectType = (projectDescription: string): ProjectTypeRecommendation[] => {
  const description = projectDescription.toLowerCase();
  const recommendations: ProjectTypeRecommendation[] = [];

  // Keywords für jede Kategorie
  const keywords = {
    [ProjectPlanningCategory.LEARNING_GOAL]: ['lernen', 'prüfung', 'kompetenz', 'ausbildung', 'skill', 'wissen', 'zertifikat'],
    [ProjectPlanningCategory.PROJECT_GOAL]: ['release', 'launch', 'deadline', 'liefern', 'projekt', 'event', 'termin'],
    [ProjectPlanningCategory.PERSONAL_GOAL]: ['persönlich', 'gewohnheit', 'fitness', 'karriere', 'entwicklung', 'ich möchte'],
    [ProjectPlanningCategory.VISION_GOAL]: ['vision', 'langfristig', 'transformation', 'zukunft', 'strategie', 'bis 2030'],
    [ProjectPlanningCategory.TECHNICAL_GOAL]: ['system', 'api', 'implementierung', 'software', 'technisch', 'code', 'architektur']
  };

  // Score berechnen für jede Kategorie
  Object.entries(keywords).forEach(([category, words]) => {
    const matches = words.filter(word => description.includes(word));
    if (matches.length > 0) {
      const confidence = Math.min(matches.length * 25, 95); // Max 95% confidence
      const categoryEnum = category as ProjectPlanningCategory;
      const typeDefinition = PROJECT_TYPE_DEFINITIONS[categoryEnum];
      
      recommendations.push({
        category: categoryEnum,
        confidence,
        reasoning: {
          keywords: matches,
          patterns: matches.map(word => `Keyword "${word}" erkannt`),
          indicators: [typeDefinition.characteristics.triggerType, typeDefinition.characteristics.timeHorizon]
        },
        explanation: `Empfehlung für ${typeDefinition.label} basierend auf ${matches.length} gefundenen Schlüsselwörtern: ${matches.join(', ')}`
      });
    }
  });

  return recommendations.sort((a, b) => b.confidence - a.confidence);
};